import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { setConversationBotActive } from '@/api/ai-engine';
import { queryKeys } from '@/lib/query-keys';
import type {
  ConversationBotUpdate,
  ConversacionResumen,
  DetalleConversacion,
  ListaConversaciones,
} from '@/types/conversations';

/**
 * Handoff: pausar/reanudar el bot en UNA conversación (módulo 4, `ai-engine`).
 *
 * Es la única escritura del inbox y es **solo ADMIN** (`requireRole('ADMIN')`): el AGENTE_HUMANO
 * lee la bandeja pero no puede quitarse ni ponerse el bot. El backend responde 403 si alguien
 * invoca el endpoint a mano, así que la pantalla esconde el interruptor cuando `canEdit` es false.
 *
 * El `PATCH` devuelve la fila CRUDA de `conversations` (sin `requiereAtencionHumana` ni el último
 * mensaje), así que NO se guarda tal cual en la caché: se reescribe el estado del bot en el
 * resumen que ya está pintado (bandeja + detalle) y además se invalida el listado, porque con el
 * bot pausado la conversación cambia de dueño en la bandeja y los filtros ("requiere atención")
 * dejan de cuadrar con lo que hay en pantalla.
 */

export interface SetBotActiveInput {
  conversationId: string;
  botActivo: boolean;
}

/**
 * Copia al resumen los campos que devuelve el `PATCH` (la fila CRUDA de `conversations`): el estado del
 * bot y el motivo/fecha de la pausa. "Requiere atención humana" es exactamente lo contrario de
 * `botActivo`, y el motivo importa: al encender desde el panel el backend lo limpia, y al apagar deja
 * `MANUAL` (así la explicación de la pausa no se queda pegada de un handoff anterior).
 */
function withBotState<T extends ConversacionResumen>(
  conversacion: T,
  cambio: Pick<ConversationBotUpdate, 'botActivo' | 'botPausadoMotivo' | 'botPausadoAt'>,
): T {
  return {
    ...conversacion,
    botActivo: cambio.botActivo,
    requiereAtencionHumana: !cambio.botActivo,
    botPausadoMotivo: cambio.botPausadoMotivo,
    botPausadoAt: cambio.botPausadoAt,
  };
}

function patchCachedConversation(
  queryClient: QueryClient,
  tenantId: string,
  cambio: ConversationBotUpdate,
): void {
  // Todas las combinaciones de filtros del listado (prefijo `lists`).
  queryClient.setQueriesData<ListaConversaciones>(
    { queryKey: queryKeys.conversations.lists(tenantId) },
    (data) =>
      data
        ? {
            ...data,
            conversaciones: data.conversaciones.map((conversacion) =>
              conversacion.id === cambio.id ? withBotState(conversacion, cambio) : conversacion,
            ),
          }
        : data,
  );

  // El detalle de la conversación abierta, si está en caché.
  queryClient.setQueryData<DetalleConversacion>(
    queryKeys.conversations.detail(tenantId, cambio.id),
    (data) => (data ? withBotState(data, cambio) : data),
  );
}

export function useSetBotActive(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, botActivo }: SetBotActiveInput) =>
      setConversationBotActive(tenantId ?? '', conversationId, botActivo),
    onSuccess: (conversacion) => {
      if (!tenantId) return;

      patchCachedConversation(queryClient, tenantId, conversacion);
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.lists(tenantId) });
    },
  });
}
